import { useState } from 'react';
import PageNavbar from '../../../components/feature/PageNavbar';
import Footer from '../../home/components/Footer';
import OnlineReferralModal from '../../home/components/OnlineReferralModal';

export default function PathProgramPage() {
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              PATH Program
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Preferred Alternatives To Hospitalisation
            </h1>
            <p className="text-2xl text-teal-600 font-semibold mb-4">
              Intensive Neuroplasticity-Based Capacity Building
            </p>
            <p className="text-xl text-gray-700 mb-8 italic">
              Rewiring Potential. Restoring Capacity. Rebuilding Life.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">
              PATH is a 2–4 week intensive residential neuroplasticity-based program designed for people living with neurological disabilities and complex life challenges. Grounded in the science of how the brain heals and adapts, PATH integrates lifestyle medicine, advanced rehabilitation technologies, and multidisciplinary therapies to build functional capacity, independence and quality of life.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* Who It's For */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Who It&rsquo;s For</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl">
            PATH supports individuals living with neurological and neurodevelopmental conditions, including:
          </p>
          <div className="max-w-4xl columns-2 md:columns-3 gap-8">
            {[
              'Intellectual Disability',
              'Multiple Sclerosis',
              'Stroke',
              'Traumatic Brain Injury',
              'Cognitive Impairment / Dementia',
              'PTSD',
              'Autism Spectrum Disorder',
              "Parkinson's Disease",
              'Cerebral Palsy',
              'Epilepsy',
              'Functional Neurological Disorder',
              'Developmental Disorders',
              'Down Syndrome',
              'Movement Disorders',
              'ADHD',
            ].map((condition, index) => (
              <p key={index} className="text-gray-700 py-1.5 break-inside-avoid">
                {condition}
              </p>
            ))}
          </div>
        </section>

        {/* What the Program Addresses */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What the Program Addresses</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl">
            The program focuses on education, functional rehabilitation and lifestyle-based therapies to improve:
          </p>
          <div className="max-w-4xl columns-2 md:columns-3 gap-8">
            {[
              'Mobility and balance',
              'Memory and cognition',
              'Emotional regulation',
              'Depression and anxiety',
              'Pain and spasticity',
              'Activities of Daily Living (ADLs)',
              'Sleep optimisation',
              'Swallowing and aspiration prevention',
              'Incontinence management',
              'Medication optimisation',
              'Relationship wellbeing',
            ].map((item, index) => (
              <p key={index} className="text-gray-700 py-1.5 break-inside-avoid">
                {item}
              </p>
            ))}
          </div>
        </section>

        {/* What's Included */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What&rsquo;s Included</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl">
            Participants may access:
          </p>
          <div className="max-w-4xl columns-1 md:columns-2 gap-8">
            {[
              'Robotics, Artificial Intelligence & Virtual Reality rehabilitation (Tyromotion Suite)',
              'Occupational Therapy and Physiotherapy',
              'Exercise Therapy',
              'Lifestyle Medicine and Nutritional Therapies',
              'Mindfulness and Stress Regulation',
              'Art, Music and Dance Therapy',
              'Tai Chi, Yoga, Ecotherapy (Shinrin Yoku)',
              'Laughter Therapy and Sound Therapy',
              'All-Abilities Sports & Recreation',
              'Writing and Reflective Therapy',
            ].map((item, index) => (
              <p key={index} className="text-gray-700 py-1.5 break-inside-avoid">
                {item}
              </p>
            ))}
          </div>
          <p className="text-gray-500 italic mt-8 text-[15px]">
            All programs are individualised.
          </p>
        </section>

        {/* How to Access */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Access</h2>
          <div className="max-w-3xl space-y-4 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              2–4 week residential programs in Brisbane
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              All-inclusive accommodation and meals
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              No out-of-pocket expenses for eligible participants
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We can assist with funding applications for your NDIS package budget
            </p>
          </div>
          <div className="max-w-3xl">
            <p className="text-lg text-gray-900 font-semibold mb-4">To apply:</p>
            <div className="flex flex-col sm:flex-row gap-8 mb-6">
              <div>
                <span className="text-sm text-gray-500 block mb-1">Email</span>
                <a
                  href="mailto:clinic@aahealth.com.au"
                  className="text-teal-700 font-semibold hover:underline cursor-pointer"
                >
                  clinic@aahealth.com.au
                </a>
              </div>
              <div>
                <span className="text-sm text-gray-500 block mb-1">Call</span>
                <a
                  href="tel:0428850470"
                  className="text-teal-700 font-semibold hover:underline cursor-pointer"
                >
                  0428 850 470
                </a>
              </div>
            </div>
            <p className="text-gray-600 text-[15px]">
              Please specify preferred program length (2, 3 or 4 weeks), condition, goals and expectations.
            </p>
          </div>
        </section>

        {/* Location */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Location</h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-4">
            Delivered in Brisbane within a dedicated multidisciplinary rehabilitation environment.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl">
            154 Ipswich Road, Woolloongabba QLD 4102
          </p>
        </section>

        {/* Program Outline */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Outline</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl">
            Each day follows a structured <strong>Intensive Multidisciplinary Treatment Program (IMTIP)</strong> model designed to optimise:
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            {[
              'Neuroplasticity',
              'Behavioural activation',
              'Functional recovery',
              'Emotional regulation',
              'Cognitive consolidation',
            ].map((item, i) => (
              <span
                key={i}
                className="border border-teal-300 text-teal-800 px-5 py-2.5 rounded-full text-sm font-medium"
              >
                {item}
              </span>
            ))}
          </div>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-2">
            The daily rhythm balances physical rehabilitation, cognitive therapy, psychoeducation, restorative modalities and experiential activities.
          </p>
          <p className="text-gray-900 font-semibold">
            Programs are tailored to participant needs.
          </p>
        </section>

        {/* Program Guide */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Guide</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">WEEK 1</th>
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">MONDAY</th>
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">TUESDAY</th>
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">WEDNESDAY</th>
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">THURSDAY</th>
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">FRIDAY</th>
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">SATURDAY</th>
                  <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900">SUNDAY</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">8.00am</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">8.30am</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Check in and Peer Support</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">9.00am</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Core Therapy Session</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">10.10am</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">10.45am</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">12.00pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Lunch &amp; Social Engagement</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">1.00pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Physical Rehabilitation Session</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">2.30pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">3.00pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Complimentary Therapies</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">4.00pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">4.15pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Group Psychoeducation</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">5.15pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Optional Fitness / Recreational Activity</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">6.00pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">BREAK</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">6.30pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Dinner &amp; Social Engagement</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2.5 font-medium text-gray-900">8.00pm</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-3 py-2.5 text-gray-700">Reflection &amp; Reset</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Terms & Conditions */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Terms &amp; Conditions</h2>
          <div className="max-w-3xl border-l-2 border-gray-200 pl-6 space-y-4">
            {[
              'Pre-program clinical review required',
              'Residential accommodation and meals included',
              'Drug and alcohol-free environment',
              'Confidentiality and peer respect required',
              'Family and support person participation available',
            ].map((term, i) => (
              <p key={i} className="text-gray-600 leading-relaxed">
                <span className="text-teal-600 font-semibold mr-2">{i + 1}.</span>
                {term}
              </p>
            ))}
          </div>
        </section>

        {/* Take the Next Step */}
        <section className="mb-16 max-w-3xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Take the Next Step</h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-2">
            Neuroplasticity means the brain can change.
          </p>
          <p className="text-xl text-teal-700 font-semibold leading-relaxed mb-8">
            PATH is designed to help you harness that change.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setIsReferralModalOpen(true)}
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-7 py-3.5 rounded-lg text-base font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Online Referral
              <i className="ri-arrow-right-line"></i>
            </button>
            <a
              href="mailto:clinic@aahealth.com.au"
              className="inline-flex items-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-700 px-7 py-3.5 rounded-lg text-base font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Email to Reserve Your Place
              <i className="ri-mail-line"></i>
            </a>
          </div>
        </section>

      </div>

      <Footer />

      <OnlineReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
      />
    </div>
  );
}
